import { useState, useCallback } from "react";
import { TwilioCallButton, PostCallModal } from "shared/components/TwilioCallButton";

interface ContactCallButtonProps {
  contactId: number;
  contactName: string;
  phoneNumber: string;
  size?: number;
  className?: string;
  onCallLogged?: () => void;
}

/**
 * Call button for contact detail pages that includes:
 * - Browser-based calling via Twilio
 * - Post-call modal to log call notes
 */
export const ContactCallButton = ({
  contactId,
  contactName,
  phoneNumber,
  size = 18,
  className = "",
  onCallLogged,
}: ContactCallButtonProps) => {
  const [showPostCallModal, setShowPostCallModal] = useState(false);
  const [lastCallDuration, setLastCallDuration] = useState<number | undefined>();
  const [lastPhoneNumber, setLastPhoneNumber] = useState<string>("");

  // Handler for when a call is completed
  const handleCallCompleted = useCallback((calledPhoneNumber: string, durationSeconds: number) => {
    console.log(`Call completed: ${calledPhoneNumber}, duration: ${durationSeconds}s`);
    setLastPhoneNumber(calledPhoneNumber);
    setLastCallDuration(durationSeconds);
    // Show the post-call modal to capture notes
    setShowPostCallModal(true);
  }, []);

  // Handler for when call is logged successfully
  const handleCallLogged = useCallback(() => {
    onCallLogged?.();
    setShowPostCallModal(false);
    setLastCallDuration(undefined);
    setLastPhoneNumber("");
  }, [onCallLogged]);

  // Handler for closing the modal
  const handleCloseModal = useCallback(() => {
    setShowPostCallModal(false);
    setLastCallDuration(undefined);
    setLastPhoneNumber("");
  }, []);

  return (
    <>
      <TwilioCallButton
        phoneNumber={phoneNumber}
        contactName={contactName}
        size={size}
        className={className}
        onCallCompleted={handleCallCompleted}
      />

      <PostCallModal
        isOpen={showPostCallModal}
        onClose={handleCloseModal}
        contactId={contactId}
        contactName={contactName}
        phoneNumber={lastPhoneNumber || phoneNumber}
        durationSeconds={lastCallDuration}
        onCallLogged={handleCallLogged}
      />
    </>
  );
};

export default ContactCallButton;
